import type {
  CriterionAverageItem,
  HeatmapMode,
  QualityZoneCode,
  StabilityZoneCode,
  TrendDirection,
} from '@/entities/dashboards';
import {
  QualityZoneColor,
  type QualityZoneRanges,
  qualityZoneByRatio,
  zoneBadgeClass,
  zoneTextClass,
} from '@/shared/lib';

/**
 * Оформление показателя вне шкалы: значения нет либо оно ни о чём не говорит.
 *
 * Серый намеренно не входит в карту цветов зон: зелёный, жёлтый и красный — это оценка,
 * а «нет данных» и «без изменений» оценки не несут и в шкале не участвуют.
 */
const NEUTRAL_BADGE_CLASS = 'bg-muted text-muted-foreground';

/**
 * Единый потолок высоты блоков рядов 1/3/4 дашборда (рейтинги, критерии, низкие оценки,
 * топ-5 звонков, красные зоны, стабильность, тренды, инсайты) — в пикселях, для блоков,
 * которым высота задаётся значением, а не классом (`DataGrid`).
 *
 * Значение подобрано так, чтобы блок топ-5 звонков помещался целиком: пять карточек звонка
 * с датой, клиентом и обращением укладываются полностью, включая перенос строки на длинных
 * ФИО и обращениях. Прежние 30rem обрезали пятую карточку при переносе описания на вторую строку.
 */
export const DASHBOARD_BLOCK_MAX_HEIGHT = 600;

/**
 * Тот же потолок ({@link DASHBOARD_BLOCK_MAX_HEIGHT}) классом Tailwind: `max-h-150` — это 37.5rem,
 * то есть те же 600 px. Более длинные блоки прокручиваются внутри карточки, не растягивая ряд.
 *
 * Класс и число обязаны совпадать: иначе соседние блоки одного ряда обрываются на разной высоте.
 */
export const DASHBOARD_BLOCK_SCROLL_CLASS = 'max-h-150 overflow-y-auto';

/**
 * Классы кликабельного блока дашборда (алерт «Красных зон», зона качества, инсайт, звонок
 * топ-5): единое выделение наведённого блока — приглушение его собственной заливки.
 *
 * Выделение заливкой, а не контуром: блоки лежат в прокручиваемых областях вплотную к краю,
 * и контур наведённого блока обрезался краем области, оставаясь виден лишь частично.
 */
export const DASHBOARD_CLICKABLE_BLOCK_CLASS = 'cursor-pointer hover:brightness-95';

/** Высота строки DataGrid-таблиц дашборда. */
const DASHBOARD_GRID_ROW_HEIGHT = 36;

/** Высота обвязки DataGrid поверх строк: шапка таблицы и её отступы. */
const DASHBOARD_GRID_CHROME_HEIGHT = 96;

/**
 * Высота DataGrid-таблиц дашборда (стабильность, тренды, отклонения мини-отчёта, критерии
 * звонка): таблица тянется по своим строкам, но не выше общего потолка блоков дашборда, чтобы
 * соседи по ряду обрывались на одной высоте.
 */
export const dashboardGridHeight = (rowCount: number): number =>
  Math.min(rowCount * DASHBOARD_GRID_ROW_HEIGHT + DASHBOARD_GRID_CHROME_HEIGHT, DASHBOARD_BLOCK_MAX_HEIGHT);

/**
 * Форматирует число с заданным количеством знаков после запятой.
 * Возвращает '—' для null/undefined/NaN.
 */
export const fmt = (v: number | null | undefined, digits = 1): string => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return v.toFixed(digits);
};

/** Средний балл по массиву чисел, игнорирует null и NaN. */
export const safeAvg = (values: (number | null)[]): number | null => {
  const filtered = values.filter((v): v is number => v !== null && !isNaN(v));
  if (filtered.length === 0) return null;
  return filtered.reduce((a, b) => a + b, 0) / filtered.length;
};

/** Группировка даты по месяцу (YYYY-MM). Возвращает '' при некорректной дате. */
export const getMonthKey = (dateStr: string | null): string => {
  if (!dateStr) return '';
  return dateStr.substring(0, 7);
};

/** Группировка даты по ISO-неделе в формате YYYY-Wnn. Возвращает '' при некорректной дате. */
export const getWeekKey = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - day);
  const y = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  const w = Math.ceil(((x.getTime() - y.getTime()) / 86400000 + 1) / 7);
  return `${x.getUTCFullYear()}-W${String(w).padStart(2, '0')}`;
};

/**
 * Границы периода тепловой карты как пара дат `YYYY-MM-DD`. Нужны, чтобы сузить
 * запрос списка звонков ячейки до её периода и не полагаться на общую (обрезанную)
 * выборку деталей. Границы считаются в UTC (без влияния таймзоны рантайма), а
 * неделя — по ISO 8601 (понедельник–воскресенье), как её считает бэкенд
 * (`isocalendar`), чтобы список ячейки соответствовал самой ячейке.
 *
 * @param period ключ периода: `YYYY-MM` (месяц) или `YYYY-Wnn` (ISO-неделя)
 * @param mode режим агрегации карты
 * @returns границы периода либо `null`, если ключ не распознан
 */
export const getPeriodDateRange = (period: string, mode: HeatmapMode): { from: string; to: string } | null => {
  const fmtDate = (d: Date): string =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

  if (mode === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(period);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]); // 1..12
    // День 0 следующего месяца == последний день текущего.
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return { from: `${m[1]}-${m[2]}-01`, to: `${m[1]}-${m[2]}-${String(lastDay).padStart(2, '0')}` };
  }

  const m = /^(\d{4})-W(\d{2})$/.exec(period);
  if (!m) return null;
  const isoYear = Number(m[1]);
  const isoWeek = Number(m[2]);
  // Понедельник ISO-недели 1 — понедельник недели, содержащей 4 января.
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7; // 1=Пн..7=Вс
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1) + (isoWeek - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { from: fmtDate(monday), to: fmtDate(sunday) };
};

/**
 * CSS-класс окраски для общего балла (шкала 0–40).
 * >= 35 — зелёный, >= 25 — жёлтый, иначе красный; цвет — из общей карты цветов зон.
 */
export const totalScoreClass = (v: number | null): string => {
  if (v === null) return NEUTRAL_BADGE_CLASS;
  if (v >= 35) return zoneBadgeClass.green;
  if (v >= 25) return zoneBadgeClass.yellow;
  return zoneBadgeClass.red;
};

/**
 * CSS-класс окраски для балла по отдельному критерию (шкала 0–5).
 * >= 4 — зелёный, >= 2 — жёлтый, иначе красный; цвет — из общей карты цветов зон.
 */
export const scoreClass = (v: number | null): string => {
  if (v === null) return NEUTRAL_BADGE_CLASS;
  if (v >= 4) return zoneBadgeClass.green;
  if (v >= 2) return zoneBadgeClass.yellow;
  return zoneBadgeClass.red;
};

/**
 * CSS-класс окраски ячейки heatmap по средней доле балла её оценок от их собственных максимумов.
 *
 * Долю считает бэкенд по каждой оценке отдельно: максимум задан экспертным заключением оценки, и
 * в одну ячейку могут попасть оценки по разным заключениям — делить средний балл на чужой максимум
 * нельзя. Без доли (пустая ячейка, неизвестная шкала) зоны нет — ячейка остаётся нейтральной.
 */
export const heatmapClass = (share: number | null): string => {
  if (share === null) return NEUTRAL_BADGE_CLASS;
  return zoneBadgeClass[qualityZoneByRatio(share)];
};

/**
 * Зона качества критерия мини-отчёта по доле его балла от максимума своей шкалы.
 *
 * Максимум приходит с бэкенда у каждого критерия: шкалы критериев различаются, поэтому
 * зона считается по доле балла, а не по абсолютному порогу. Без балла или максимума зоны
 * нет — оценки не существует. Пороги зон настраиваются пользователем (настройки отображения);
 * без них берутся пороги по умолчанию.
 */
export const critZoneByMax = (
  score: number | null,
  max: number | null,
  ranges?: QualityZoneRanges,
): QualityZoneColor | null => {
  if (score === null || max === null || max <= 0) return null;
  return qualityZoneByRatio(score / max, ranges);
};

/** CSS-класс окраски ячейки критерия мини-отчёта по его зоне; без зоны — нейтральный. */
export const critCellClass = (
  score: number | null,
  max: number | null,
  ranges?: QualityZoneRanges,
): string => {
  const zone = critZoneByMax(score, max, ranges);
  return zone ? zoneBadgeClass[zone] : NEUTRAL_BADGE_CLASS;
};

/**
 * Доля среднего балла критерия от максимума его шкалы — основа для сравнения критериев с разными
 * шкалами. `null`, если максимум не определён (`maxScore` пуст или ≤ 0): долю посчитать нельзя.
 */
export const criterionScoreRatio = (item: CriterionAverageItem): number | null =>
  item.maxScore !== null && item.maxScore > 0 ? item.value / item.maxScore : null;

/**
 * Слабейший и сильнейший критерии по ДОЛЕ балла от максимума своей шкалы, а не по абсолютному
 * среднему: критерий с короткой шкалой у своего потолка не выглядит слабым лишь из-за малого
 * абсолюта, а критерий с длинной шкалой — сильным лишь из-за большого. Учитываются только критерии
 * с определимым максимумом (`maxScore > 0`) — без него долю не посчитать; пустой вход → оба `null`.
 */
export const criteriaExtremesByRatio = (
  items: CriterionAverageItem[],
): { weakest: CriterionAverageItem | null; strongest: CriterionAverageItem | null } => {
  let weakest: CriterionAverageItem | null = null;
  let strongest: CriterionAverageItem | null = null;
  let minRatio = Number.POSITIVE_INFINITY;
  let maxRatio = Number.NEGATIVE_INFINITY;
  for (const item of items) {
    const ratio = criterionScoreRatio(item);
    if (ratio === null) continue;
    if (ratio < minRatio) {
      minRatio = ratio;
      weakest = item;
    }
    if (ratio > maxRatio) {
      maxRatio = ratio;
      strongest = item;
    }
  }
  return { weakest, strongest };
};

/** Порог дельты, ниже которого отклонение считается незначимым и зоны не имеет. */
const DELTA_THRESHOLD = 0.2;

/**
 * CSS-класс окраски дельты (отклонения): рост — зелёный, падение — красный.
 * Заливки у дельты нет — из карты цветов зон берётся только цвет текста.
 */
export const deltaClass = (v: number | null): string => {
  if (v === null) return 'text-muted-foreground';
  if (v > DELTA_THRESHOLD) return `${zoneTextClass.green} font-bold`;
  if (v < -DELTA_THRESHOLD) return `${zoneTextClass.red} font-bold`;
  return 'text-muted-foreground font-bold';
};

/**
 * Текстовое представление дельты со стрелкой направления.
 * Примеры: «↑ +1.5», «↓ -0.8», «→ 0.1», «—».
 */
export const deltaText = (v: number | null): string => {
  if (v === null) return '—';
  if (v > DELTA_THRESHOLD) return `↑ +${v.toFixed(1)}`;
  if (v < -DELTA_THRESHOLD) return `↓ ${v.toFixed(1)}`;
  return `→ ${v.toFixed(1)}`;
};

/** Зона стабильности консультанта → зона цветовой шкалы (стабилен — зелёный, нестабилен — красный). */
export const stabilityZoneColor: Record<StabilityZoneCode, QualityZoneCode> = {
  stable: 'green',
  moderate: 'yellow',
  unstable: 'red',
};

/** Классы бейджа направления тренда: рост — зелёный, падение — красный, без изменений — серый. */
export const trendBadgeClass: Record<TrendDirection, string> = {
  up: zoneBadgeClass.green,
  flat: NEUTRAL_BADGE_CLASS,
  down: zoneBadgeClass.red,
};

/** Стрелка-индикатор направления недельного тренда. */
export const trendArrow: Record<TrendDirection, string> = {
  up: '↑',
  flat: '→',
  down: '↓',
};

/** Форматирует знаковую величину: положительное значение выводится с ведущим «+». */
export const fmtSigned = (v: number, digits = 1): string => `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;
